/**
 * SonmiEmployees — hidden admin route at `/admin/employees`.
 *
 * The smallest employee registry that lets SONMI resolve FT/PT at
 * payment time. Every status change is versioned with a reason so a
 * mid-schedule promotion is provable years later.
 *
 * Not linked from the manager sidebar — reached by direct URL only.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import OpenButton from "@/components/toto/OpenButton";
import { UserPlus, Users } from "lucide-react";
import { sonmiCopy } from "@/lib/sonmiCopy";
import { useNavigate } from "react-router-dom";

const BOUTIQUES = ["PLT", "CRS", "AZR", "MRB", "MGN", "OBS", "OPL"];

export default function SonmiEmployees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/sonmi/employees`);
      setRows(r.data?.employees || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't load employees.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    setBusy(true);
    try {
      const r = await api.post(`/sonmi/employees`, form);
      toast.success(sonmiCopy.toast.employeeCreated);
      setShowForm(false);
      await load();
      // Take the admin straight to the SONMI offer for this new MT.
      navigate(`/admin/sonmi/sign-on-bonus?employee=${r.data.employee.employee_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't create.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Topbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
        <header className="mb-6">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h1 className="text-3xl sm:text-4xl font-semibold text-stone-900">
              {sonmiCopy.page.employees.title}
            </h1>
            {!showForm && (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-stone-900 hover:bg-stone-800"
                data-testid="add-employee-btn"
              >
                <UserPlus className="w-4 h-4" /> {sonmiCopy.page.employees.add}
              </button>
            )}
          </div>
          <p className="mt-3 text-stone-600 max-w-2xl">
            {sonmiCopy.page.employees.lede}
          </p>
        </header>

        {showForm && (
          <EmployeeForm busy={busy} onSubmit={create} onCancel={() => setShowForm(false)} />
        )}

        {rows.length === 0 && !showForm ? (
          <div className="mt-6 text-stone-500 flex items-center gap-2">
            <Users className="w-5 h-5" /> {sonmiCopy.page.employees.empty}
          </div>
        ) : (
          <div className="mt-6 space-y-2" data-testid="employees-list">
            {rows.map((e) => (
              <EmployeeRow
                key={e.employee_id}
                employee={e}
                onOpenSignon={() => navigate(`/admin/sonmi/sign-on-bonus?employee=${e.employee_id}`)}
                onChanged={load}
              />
            ))}
          </div>
        )}

        <div className="mt-10 text-xs text-stone-500">
          {sonmiCopy.common.signedInAs(user?.email || "unknown")}
        </div>
      </div>
    </div>
  );
}


function EmployeeRow({ employee, onOpenSignon, onChanged }) {
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const [form, setForm] = useState({
    employment_status: employee.employment_status === "FT" ? "PT" : "FT",
    effective_date: new Date().toISOString().slice(0, 10),
    reason: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/sonmi/employees/${employee.employee_id}/status`, form);
      toast.success(sonmiCopy.toast.statusChanged);
      setChanging(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't change status.");
    } finally {
      setBusy(false);
    }
  };

  const statusHistory = (employee.history || []).filter(
    (h) => h.field === "employment_status"
  );

  return (
    <div
      className="rounded-xl border border-stone-200 bg-white overflow-hidden"
      data-testid={`employee-row-${employee.employee_id}`}
    >
      <div className="px-5 py-4 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 text-left"
          data-testid={`employee-toggle-${employee.employee_id}`}
        >
          <div className="text-sm font-medium text-stone-900">{employee.display_name}</div>
          <div className="text-xs text-stone-500 mt-0.5">
            {employee.boutique_code} ·{" "}
            {employee.role || "Massage Therapist"} · {" "}
            {employee.employment_status === "FT" ? "Full-time" : "Part-time"} since{" "}
            {employee.employment_status_effective_date} · hired {employee.hire_date}
          </div>
        </button>
        <OpenButton onClick={onOpenSignon} aria-label={`Open sign-on bonus for ${employee.name || employee.employee_id}`} data-testid={`open-signon-${employee.employee_id}`}/>
      </div>

      {open && (
        <div className="border-t border-stone-100 px-5 py-4 bg-stone-50/40">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">
            Employment status history
          </div>
          <ul className="space-y-1">
            {statusHistory.map((h, i) => (
              <li key={i} className="text-sm text-stone-700"
                  data-testid={`status-history-entry-${employee.employee_id}-${i}`}>
                <span className="font-variant-numeric tabular-nums">
                  {h.effective}
                </span>
                <span className="mx-2 text-stone-400">·</span>
                <strong>{h.to === "FT" ? "Full-time" : "Part-time"}</strong>
                {h.from && (
                  <span className="text-stone-500"> (was {h.from === "FT" ? "FT" : "PT"})</span>
                )}
                {h.reason && <span className="text-stone-500 italic"> — {h.reason}</span>}
              </li>
            ))}
          </ul>

          {!changing ? (
            <button
              type="button"
              onClick={() => setChanging(true)}
              className="mt-3 text-sm text-stone-700 hover:text-stone-900 underline underline-offset-2"
              data-testid={`change-status-btn-${employee.employee_id}`}
            >
              Record a status change
            </button>
          ) : (
            <div className="mt-3 rounded-lg border border-stone-200 bg-white p-4">
              <div className="text-sm text-stone-700 mb-3">
                Record a change to <strong>{employee.display_name}</strong>'s
                employment status. SONMI will resolve the status from history at every
                payment date, so past ledger entries stay correct.
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">
                    New status
                  </label>
                  <div className="flex gap-2">
                    {["FT", "PT"].map((v) => (
                      <button
                        key={v} type="button"
                        onClick={() => setForm((f) => ({ ...f, employment_status: v }))}
                        className={
                          "flex-1 px-3 py-1.5 rounded-lg border text-sm " +
                          (form.employment_status === v
                            ? "border-stone-900 bg-stone-900 text-white"
                            : "border-stone-300 text-stone-700 hover:border-stone-500")
                        }
                        data-testid={`change-status-${employee.employee_id}-${v}`}
                      >
                        {v === "FT" ? "Full-time" : "Part-time"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">
                    Effective date
                  </label>
                  <input
                    type="date" value={form.effective_date}
                    onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-stone-300 rounded-lg text-sm"
                    data-testid={`change-effective-date-${employee.employee_id}`}
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">
                    Reason
                  </label>
                  <input
                    type="text" value={form.reason} placeholder="promotion, dispute, correction…"
                    onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-stone-300 rounded-lg text-sm"
                    data-testid={`change-reason-${employee.employee_id}`}
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button" onClick={() => setChanging(false)}
                  className="px-3 py-1.5 rounded-lg text-sm text-stone-600 hover:bg-stone-100"
                >
                  Cancel
                </button>
                <button
                  type="button" onClick={submit} disabled={busy}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300"
                  data-testid={`change-submit-${employee.employee_id}`}
                >
                  {busy ? "Saving…" : "Record change"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function EmployeeForm({ busy, onSubmit, onCancel }) {
  const [name, setName] = useState("");
  const [boutique, setBoutique] = useState("PLT");
  const [status, setStatus] = useState("FT");
  const [hireDate, setHireDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const canSubmit = name.trim().length >= 2 && hireDate && !busy;

  return (
    <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            Name
          </label>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jordan Smith"
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
            data-testid="employee-name-input"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            Boutique
          </label>
          <select
            value={boutique} onChange={(e) => setBoutique(e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white"
            data-testid="employee-boutique-select"
          >
            {BOUTIQUES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            Status at hire
          </label>
          <div className="flex gap-2">
            {["FT", "PT"].map((v) => (
              <button
                key={v} type="button" onClick={() => setStatus(v)}
                className={
                  "flex-1 px-3 py-2 rounded-lg border text-sm " +
                  (status === v
                    ? "border-stone-900 bg-stone-900 text-white"
                    : "border-stone-300 text-stone-700 hover:border-stone-500")
                }
                data-testid={`employee-status-${v}`}
              >
                {v === "FT" ? "Full-time" : "Part-time"}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            Hire date
          </label>
          <input
            type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
            data-testid="employee-hire-date-input"
          />
        </div>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button" onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-sm text-stone-600 hover:bg-stone-100"
        >
          {sonmiCopy.common.cancel}
        </button>
        <button
          type="button" disabled={!canSubmit}
          onClick={() => onSubmit({
            display_name: name.trim(),
            boutique_code: boutique,
            employment_status: status,
            hire_date: hireDate,
          })}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300"
          data-testid="employee-create-submit"
        >
          {busy ? "Saving…" : sonmiCopy.common.save}
        </button>
      </div>
    </div>
  );
}
